/**
 * Integrytas Group - Common Security Module (CSM)
 * useBranding — fetches live branding from csm_system_parameters.
 *
 * Post-auth: reads INTEGRYTAS.APP.NAME (plain text) and
 * INTEGRYTAS.APP.BRANDING_SUFFIX (Base64-encoded) from the DB and returns
 * decoded values. Falls back to the hard-coded build-time constants when the
 * fetch fails (e.g., pre-auth / anon context where RLS blocks access).
 */

import { useEffect, useState } from 'react';
import { apiDb } from './api_client';
import {
  DEFAULT_APP_NAME,
  BRANDING_SUFFIX,
  APP_NAME_PARAM_KEY,
  BRANDING_SUFFIX_PARAM_KEY,
  decodeBrandingValue,
} from '../constants/branding';

export interface BrandingState {
  appName: string;
  brandingSuffix: string;
  watermarkText: string;
}

export function useBranding(): BrandingState {
  const [appName, setAppName]         = useState(DEFAULT_APP_NAME);
  const [suffix, setSuffix]           = useState(BRANDING_SUFFIX);

  useEffect(() => {
    apiDb
      .from('csm_system_parameters')
      .select('internal_code,parameter_value')
      .in('internal_code', [APP_NAME_PARAM_KEY, BRANDING_SUFFIX_PARAM_KEY])
      .eq('is_active', 'Y')
      .then(({ data }) => {
        if (!data) return;
        for (const row of data) {
          if (row.internal_code === APP_NAME_PARAM_KEY && row.parameter_value)
            setAppName(row.parameter_value);
          if (row.internal_code === BRANDING_SUFFIX_PARAM_KEY && row.parameter_value)
            setSuffix(decodeBrandingValue(row.parameter_value));
        }
      });
  }, []);

  // Keep the browser tab on the live app name. main.tsx seeds it from the
  // build-time constant before mount; once the DB value resolves, the tab
  // follows it exactly as the watermark does. Both shells (desktop and mobile)
  // call this hook, so the behaviour is shared rather than re-implemented per
  // surface — this is data/logic, not presentation (§16).
  useEffect(() => {
    if (appName) document.title = appName;
  }, [appName]);

  return { appName, brandingSuffix: suffix, watermarkText: `${appName} ${suffix}` };
}
